/**
 * 최근 활동 로그 카드
 */

import { useState, useEffect } from "react"
import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import Icon from "@mui/material/Icon"
import { request } from "app/network"

interface RecentActivity {
    actionType: string
    targetType: string
    targetId: string
    description: string
    createdAt: string
}

function RecentActivitiesCard(): JSX.Element {
    const [activities, setActivities] = useState<RecentActivity[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        loadActivities()
    }, [])

    const loadActivities = async () => {
        try {
            setLoading(true)
            const response = await request("User.getMyRecentActivities", {
                paramz: { limit: 5 }
            })

            if (response.status === 200 && response.data) {
                setActivities(response.data)
            }
        } catch (error) {
            console.error("최근 활동 로드 실패:", error)
        } finally {
            setLoading(false)
        }
    }

    const getActionIcon = (actionType: string) => {
        switch (actionType) {
            case "CREATE":
                return "add_circle"
            case "UPDATE":
                return "edit"
            case "DELETE":
                return "delete"
            default:
                return "circle"
        }
    }

    const getActionColor = (actionType: string) => {
        switch (actionType) {
            case "CREATE":
                return "success"
            case "UPDATE":
                return "info"
            case "DELETE":
                return "error"
            default:
                return "text"
        }
    }

    const getActionText = (actionType: string) => {
        switch (actionType) {
            case "CREATE":
                return "생성"
            case "UPDATE":
                return "수정"
            case "DELETE":
                return "삭제"
            default:
                return actionType
        }
    }

    const getTimeAgo = (dateString: string) => {
        const date = new Date(dateString)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMins / 60)
        const diffDays = Math.floor(diffHours / 24)

        if (diffMins < 1) return "방금 전"
        if (diffMins < 60) return `${diffMins}분 전`
        if (diffHours < 24) return `${diffHours}시간 전`
        if (diffDays < 7) return `${diffDays}일 전`
        return date.toLocaleDateString("ko-KR")
    }

    if (loading) {
        return (
            <Card sx={{ height: "100%", boxShadow: "none" }}>
                <MDBox p={2}>
                    <MDTypography variant="body2" color="text">
                        로딩 중...
                    </MDTypography>
                </MDBox>
            </Card>
        )
    }

    return (
        <Card sx={{ height: "100%", boxShadow: "none" }}>
            <MDBox p={2}>
                <MDBox mb={2} display="flex" alignItems="center">
                    <Icon fontSize="medium" color="info">history</Icon>
                    <MDTypography variant="h6" fontWeight="medium" ml={1}>
                        최근 활동
                    </MDTypography>
                </MDBox>

                {activities.length === 0 ? (
                    <MDBox py={2} textAlign="center">
                        <MDTypography variant="button" color="text">
                            최근 활동이 없습니다
                        </MDTypography>
                    </MDBox>
                ) : (
                    <MDBox>
                        {activities.map((activity, index) => (
                            <MDBox
                                key={index}
                                display="flex"
                                alignItems="flex-start"
                                mb={index < activities.length - 1 ? 2 : 0}
                                pb={index < activities.length - 1 ? 2 : 0}
                                borderBottom={index < activities.length - 1 ? "1px solid" : "none"}
                                sx={{ borderColor: "grey.300" }}
                            >
                                <Icon
                                    fontSize="small"
                                    color={getActionColor(activity.actionType) as any}
                                    sx={{ mr: 1.5, mt: 0.25 }}
                                >
                                    {getActionIcon(activity.actionType)}
                                </Icon>
                                <MDBox flex={1}>
                                    <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                                        <MDTypography variant="button" fontWeight="regular" color="text">
                                            {activity.description ||
                                                `${activity.targetType} ${getActionText(activity.actionType)}`}
                                        </MDTypography>
                                        <MDTypography
                                            variant="caption"
                                            color="text"
                                            sx={{ ml: 1, flexShrink: 0 }}
                                        >
                                            {getTimeAgo(activity.createdAt)}
                                        </MDTypography>
                                    </MDBox>
                                    {activity.targetId && (
                                        <MDTypography variant="caption" color="text" opacity={0.7}>
                                            ID: {activity.targetId}
                                        </MDTypography>
                                    )}
                                </MDBox>
                            </MDBox>
                        ))}
                    </MDBox>
                )}
            </MDBox>
        </Card>
    )
}

export default RecentActivitiesCard
